import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { safeDestination } from '@/core/auth/destination';
import { useConfig, useText } from '@/core/config/ConfigProvider';
import { request } from '@/core/http/client';
import { platformApiUrl } from '@/core/config/env';
import { Button, Container, Lookup, Select } from '@/design-system';
import type { SectionComponentProps } from '@/core/sections/section-registry';

/**
 * HERO COM BUSCA
 *
 * O padrão de portal imobiliário: a busca fica DENTRO do hero, não abaixo
 * dele. Quem chega já sabe o que quer; obrigar a rolar para achar o campo
 * custa visita.
 *
 * Os três campos (estado, cidade, tipo) são o mínimo que qualifica uma
 * busca de lote. Os demais filtros vivem na página de resultados — colocar
 * quinze campos aqui derruba a conversão em vez de melhorá-la.
 */

type Option = { value: string; label: string; slug: string; listingCount: number };

const acervo = { baseUrl: platformApiUrl };

/**
 * Estados e cidades vêm do ACERVO, e o que a busca leva adiante é o SLUG.
 *
 * Antes a home mandava o slug e o acervo comparava com o nome gravado. As duas
 * pontas pareciam concordar e não concordavam: buscar "Divinópolis" achava
 * ZERO dos dois anúncios que existiam, e "Itaúna" achava um dos seis. A página
 * de resultados abria, sem erro nenhum, dizendo que não havia nada — que é o
 * pior formato possível de defeito, porque parece uma resposta.
 *
 * Hoje o acervo compara pelo slug e aceita as duas grafias. A busca manda o
 * slug mesmo assim, porque ele é o que fica bonito e estável na URL.
 */
function useLocalidades(estado: string) {
  const estados = useQuery({
    queryKey: ['localidades', 'estados'],
    queryFn: () => request<Option[]>('/localidades/estados', acervo),
    staleTime: 30 * 60_000,
  });

  const cidades = useQuery({
    queryKey: ['localidades', 'cidades', estado],
    queryFn: () =>
      request<Option[]>('/localidades/cidades', {
        ...acervo,
        /*
          A LISTA É COMPLETA — todos os municípios do estado.

          Vinha com `comAnuncio=true`, que escondia as cidades sem anúncio
          para ninguém cair numa página vazia. A intenção era boa e o efeito
          era outro: num estado com 853 municípios, ver três não parece um
          catálogo novo, parece um site que não atende a sua região.

          Agora aparecem todas, e as vazias vão MARCADAS. A pessoa vê que o
          lugar existe e que ainda não há nada lá — que é uma informação, ao
          contrário do silêncio.
        */
        query: { estado },
      }),
    enabled: estado !== '',
    staleTime: 30 * 60_000,
  });

  return { estados: estados.data ?? [], cidades: cidades.data ?? [] };
}

export function HeroSearch({ section }: SectionComponentProps) {
  const { site, taxonomies } = useConfig();
  const t = useText();
  const navigate = useNavigate();

  const [estado, setEstado] = useState('');
  const [cidade, setCidade] = useState('');
  const [aptidao, setAptidao] = useState('');

  const { estados, cidades } = useLocalidades(estado);

  const finalidades = useMemo(
    () => (taxonomies.aptidao ?? []).filter((option) => option.enabled).slice(0, 8),
    [taxonomies.aptidao],
  );

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const params = new URLSearchParams();
    if (cidade) params.set('cidade', cidade);
    if (aptidao) params.set('aptidao', aptidao);
    navigate(`/lotes${params.toString() ? `?${params}` : ''}`);
  };

  const stats = (section.props.stats ?? []) as { value: string; label: string }[];

  /*
    BANNER DE FUNDO, editável na identidade do site.

    Sem imagem, a hero continua sendo a cor da marca com a malha cadastral —
    que é o padrão e funciona. Com imagem, três coisas mudam juntas, e nenhuma
    é enfeite:

    1. A cor da marca vira o FUNDO DE RESERVA. Ela aparece enquanto a foto
       carrega e se a foto falhar; sem ela, o texto branco cairia sobre branco
       no meio segundo em que a conexão engasga.

    2. Entra um véu escuro por cima. Texto branco sobre foto arbitrária é
       ilegível em metade das fotos — e quem escolhe a foto no painel não tem
       como prever o contraste em cada tela. O véu é regulável, e o padrão
       (55%) é o que mantém o título legível sobre uma foto clara de terreno ao
       meio-dia.

    3. A malha some. Ela é textura sobre cor chapada; sobre foto, vira sujeira.
  */
  const banner = (section.props.backgroundImageUrl ?? null) as string | null;
  const veu = Math.min(100, Math.max(0, Number(section.props.overlay ?? 55))) / 100;
  const posicao = (section.props.backgroundPosition ?? 'center') as string;

  /*
    DOIS MODOS DE IMAGEM, e a diferenca nao e estetica.

    `cover` e o que sempre existiu: uma FOTO de fundo, cortada para preencher
    a secao, com veu escuro por cima e o nosso titulo em branco. Serve para
    fotografia — paisagem, loteamento, terreno.

    `banner` e para arte PRONTA: a peca que ja vem com titulo, subtitulo,
    argumentos e ate botao, feita por quem desenhou a campanha. Nela, tudo o
    que a hero faz por cima e estrago — o corte esconde metade da arte, o veu
    apaga as cores, e o nosso titulo empilha uma segunda manchete sobre a que
    ja esta la.

    Entao no modo banner a imagem aparece INTEIRA, na proporcao dela, sem veu
    e sem texto sobreposto. O formulario de busca desce para baixo da arte, em
    vez de cobri-la: ele e o motivo de a hero existir e nao pode sumir, mas
    tambem nao pode ser posto em cima do que a peca tem a dizer.
  */
  const modo = (section.props.imageMode ?? 'cover') as string;
  const arteFechada = banner !== null && modo === 'banner';

  /*
    O DESTINO DO BANNER É VALIDADO AQUI, de novo.

    O painel já recusa endereço externo ao salvar, mas esta é a validação
    que conta: a configuração vem do banco e pode ter sido gravada por
    outra versão do painel, por importação, ou à mão. Confiar no que está
    guardado transformaria o banner da home — a área mais visível do site
    — num trampolim para fora do domínio.

    `safeDestination` é a MESMA função que protege o `?destino=` do
    cadastro. Ela recusa `https://…`, `//outro-dominio`, `javascript:` e as
    grafias exóticas que cada navegador normaliza de um jeito — quem
    decide se é interno é o parser do próprio navegador, comparando a
    origem resolvida com a nossa.

    Link inválido não vira erro na tela: o banner apenas deixa de ser
    clicável. Ele é arte, e continua cumprindo o papel dele sem o clique.
  */
  const destino = safeDestination(
    typeof section.props.linkUrl === 'string' ? section.props.linkUrl : null,
  );

  const formulario = (
        <form
          onSubmit={submit}
          className="w-full max-w-4xl rounded-(--radius-card) bg-surface p-3 text-left shadow-(--shadow-lift)"
          role="search"
          aria-label="Buscar lotes e terrenos"
        >
          <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_1fr_auto]">
            <label className="flex flex-col gap-1">
              <span className="sr-only">Estado</span>
              <Lookup
                value={estado}
                aria-label="Estado"
                placeholder="Estado"
                emptyMessage="Nenhum estado com esse nome."
                options={estados.map((option) => ({ value: option.value, label: option.label }))}
                onChange={(escolhido) => {
                  setEstado(escolhido ?? '');
                  setCidade(''); // trocar de estado invalida a cidade escolhida
                }}
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="sr-only">Cidade</span>
              {/*
                LOOKUP, e não `<select>`.

                A lista virou os municípios inteiros do estado — 853 em Minas.
                Rolar isso num celular não é escolher, é procurar agulha; e o
                salto por primeira letra do seletor nativo pula, não pesquisa.
                Aqui a pessoa digita "aguas" e acha "Águas Formosas", com ou
                sem acento.

                O SLUG vai para a URL: `?cidade=para-de-minas` em vez de
                `?cidade=Par%C3%A1%20de%20Minas`. O acervo compara pelo slug e
                aceita as duas formas, então a escolha aqui é só sobre o
                endereço ficar legível.
              */}
              <Lookup
                value={cidade}
                aria-label="Cidade"
                disabled={estado === ''}
                placeholder={estado === '' ? 'Cidade' : `Procurar entre ${cidades.length} cidades`}
                emptyMessage="Nenhuma cidade com esse nome neste estado."
                options={cidades.map((option) => ({
                  value: option.slug,
                  label: option.label,
                  hint: option.listingCount === 0
                    ? 'sem anúncios'
                    : option.listingCount === 1
                      ? '1 anúncio'
                      : `${option.listingCount} anúncios`,
                }))}
                onChange={(escolhida) => setCidade(escolhida ?? '')}
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="sr-only">Finalidade</span>
              <Select value={aptidao} onChange={(event) => setAptidao(event.target.value)} aria-label="Finalidade">
                <option value="">Qualquer finalidade</option>
                {finalidades.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </label>

            <Button type="submit" size="lg" className="md:px-8">
              Buscar
            </Button>
          </div>
        </form>
  );

  if (arteFechada) {
    return (
      <section className="bg-surface">
        {/*
          `<img>` de verdade, e nao fundo em CSS.

          Fundo em CSS precisa de altura declarada, e altura declarada volta a
          cortar a arte na primeira tela de proporcao diferente. Com `<img>` a
          altura vem da propria imagem, e nada e cortado em largura nenhuma.

          O `alt` fica vazio de proposito: e uma peca decorativa cujo texto
          esta desenhado dentro dela. Um leitor de tela lendo "banner" nao
          acrescenta nada, e ler o nome do arquivo seria pior.
        */}
        {destino ? (
          /*
            `alt` com texto AQUI, e vazio quando não há link.

            Um link precisa de nome acessível: sem ele, o leitor de tela
            anuncia "link" e nada mais, e a pessoa não tem como saber se
            vale seguir. Sem link a imagem é decorativa e o `alt` vazio
            continua certo — ver o comentário abaixo.
          */
          <Link to={destino} className="block">
            <img
              src={banner}
              alt={section.title ?? 'Ver mais'}
              className="w-full"
            />
          </Link>
        ) : (
          <img src={banner} alt="" className="w-full" />
        )}

        <Container>
          <div className="flex flex-col items-center gap-4 py-6">{formulario}</div>
        </Container>
      </section>
    );
  }

  return (
    <section className="relative overflow-hidden bg-primary text-primary-fg">
      {banner ? (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-cover bg-no-repeat"
            style={{ backgroundImage: `url(${banner})`, backgroundPosition: posicao }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{ backgroundColor: `rgb(0 0 0 / ${veu})` }}
          />
        </>
      ) : (
        /*
          Malha cadastral ao fundo: a folha de levantamento topográfico é o
          objeto do produto. Fica em `opacity-[0.07]` — presente para quem
          procura, invisível para quem só lê.
        */
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(0deg, transparent 0 47px, currentColor 47px 48px), repeating-linear-gradient(90deg, transparent 0 47px, currentColor 47px 48px)',
          }}
        />
      )}

      <Container className="relative">
        <div className="flex flex-col items-center gap-8 py-14 text-center md:py-20">
          <div className="flex max-w-3xl flex-col gap-4">
            <h1 className="font-display text-3xl font-extrabold leading-[1.1] md:text-5xl">
              {section.title ?? t('home.hero.title', undefined, 'Encontre o lote certo, sem rodeio.')}
            </h1>
            <p className="text-pretty text-base opacity-90 md:text-lg">
              {section.subtitle ?? t('home.hero.subtitle', undefined, '')}
            </p>
          </div>

          {formulario}

          {stats.length > 0 ? (
            <dl className="flex flex-wrap justify-center gap-x-10 gap-y-4">
              {stats.map((stat) => (
                <div key={stat.label} className="flex flex-col">
                  <dt className="sr-only">{stat.label}</dt>
                  <dd className="tnum font-display text-2xl font-bold md:text-3xl">{stat.value}</dd>
                  <span className="text-xs uppercase tracking-[0.12em] opacity-80">{stat.label}</span>
                </div>
              ))}
            </dl>
          ) : null}

          <p className="text-sm opacity-85">
            Só lotes, terrenos, glebas e áreas — nada de casa e apartamento no meio. {site.brand.name} é portal de nicho.
          </p>
        </div>
      </Container>
    </section>
  );
}
